import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../tokens';

// Button variants
const variants = {
  primary: css`
    background: ${theme.gradients.primary};
    color: ${theme.colors.text.inverse};
    border: none;

    &:hover:not(:disabled) {
      background: ${theme.gradients.primaryHover};
      box-shadow: ${theme.shadows.primary};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
  secondary: css`
    background: ${theme.colors.neutral[0]};
    color: ${theme.colors.primary[500]};
    border: 2px solid ${theme.colors.primary[500]};

    &:hover:not(:disabled) {
      background: ${theme.colors.primary[50]};
    }
  `,
  outline: css`
    background: transparent;
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.default};

    &:hover:not(:disabled) {
      border-color: ${theme.colors.border.dark};
      background: ${theme.colors.background.secondary};
    }
  `,
  ghost: css`
    background: transparent;
    color: ${theme.colors.text.primary};
    border: none;

    &:hover:not(:disabled) {
      background: ${theme.colors.neutral[100]};
    }
  `,
  danger: css`
    background: ${theme.colors.error[500]};
    color: ${theme.colors.text.inverse};
    border: none;

    &:hover:not(:disabled) {
      background: ${theme.colors.error[600]};
      box-shadow: ${theme.shadows.error};
    }
  `,
  success: css`
    background: ${theme.colors.success[500]};
    color: ${theme.colors.text.inverse};
    border: none;

    &:hover:not(:disabled) {
      background: ${theme.colors.success[600]};
      box-shadow: ${theme.shadows.success};
    }
  `,
  premium: css`
    background: ${theme.gradients.premium};
    color: ${theme.colors.text.inverse};
    border: none;

    &:hover:not(:disabled) {
      box-shadow: ${theme.shadows.premium};
    }
  `,
  link: css`
    background: transparent;
    color: ${theme.colors.text.link};
    border: none;
    padding: 0;
    height: auto;
    min-height: auto;

    &:hover:not(:disabled) {
      color: ${theme.colors.text.linkHover};
      text-decoration: underline;
    }
  `,
};

// Button sizes
const sizes = {
  xs: css`
    height: 28px;
    min-height: 28px;
    padding: 0 ${theme.spacing[2]};
    font-size: ${theme.fontSizes.xs};
    border-radius: ${theme.radii.md};
  `,
  sm: css`
    height: 36px;
    min-height: 36px;
    padding: 0 ${theme.spacing[3]};
    font-size: ${theme.fontSizes.sm};
    border-radius: ${theme.radii.md};
  `,
  md: css`
    height: 44px;
    min-height: 44px;
    padding: 0 ${theme.spacing[5]};
    font-size: ${theme.fontSizes.sm};
    border-radius: ${theme.radii.lg};
  `,
  lg: css`
    height: 52px;
    min-height: 52px;
    padding: 0 ${theme.spacing[8]};
    font-size: ${theme.fontSizes.base};
    border-radius: ${theme.radii.lg};
  `,
  xl: css`
    height: 60px;
    min-height: 60px;
    padding: 0 ${theme.spacing[10]};
    font-size: ${theme.fontSizes.lg};
    border-radius: ${theme.radii.xl};
  `,
};

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  font-family: ${theme.fontFamilies.sans};
  font-weight: ${theme.fontWeights.semibold};
  letter-spacing: ${theme.letterSpacings.wide};
  cursor: pointer;
  transition: ${theme.transitions.button};
  text-decoration: none;
  white-space: nowrap;
  user-select: none;

  /* Apply variant styles */
  ${({ $variant }) => variants[$variant] || variants.primary}

  /* Apply size styles */
  ${({ $size }) => sizes[$size] || sizes.md}

  /* Full width */
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  /* Icon only */
  ${({ $iconOnly, $size }) =>
    $iconOnly &&
    css`
      padding: 0;
      width: ${$size === 'xs' ? '28px' : $size === 'sm' ? '36px' : $size === 'lg' ? '52px' : $size === 'xl' ? '60px' : '44px'};
    `}

  /* Loading state */
  ${({ $loading }) =>
    $loading &&
    css`
      position: relative;
      color: transparent;
      pointer-events: none;

      &::after {
        content: '';
        position: absolute;
        width: 18px;
        height: 18px;
        border: 2px solid currentColor;
        border-right-color: transparent;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `}

  /* Disabled state */
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Focus state */
  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }

  /* Icon sizing */
  svg {
    width: 1.25em;
    height: 1.25em;
    flex-shrink: 0;
  }
`;

export const Button = React.forwardRef(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      iconOnly = false,
      loading = false,
      disabled = false,
      leftIcon,
      rightIcon,
      as,
      ...props
    },
    ref
  ) => {
    return (
      <StyledButton
        ref={ref}
        as={as}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $iconOnly={iconOnly}
        $loading={loading}
        disabled={disabled || loading}
        {...props}
      >
        {leftIcon && !loading && leftIcon}
        {children}
        {rightIcon && !loading && rightIcon}
      </StyledButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
